// src/client/components/RoleWatcher.tsx
import { useEffect, useRef } from "react";
import { api } from "../lib/api";
import { ROUTE } from "../lib/paths";
import { normalizeRole, type Role } from "../lib/roles";

export default function RoleWatcher() {
  const myIdRef = useRef<number | null>(null);
  const prevRoleRef = useRef<Role | undefined>(undefined);
  const wsRef = useRef<WebSocket | null>(null);
  const pingRef = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const me = await api.me();
        if (!alive) return;
        myIdRef.current = me.me?.id ?? null;
        const role = normalizeRole(me.role);
        prevRoleRef.current = role;
        routeForRole(role, false);
      } catch {}
    })();

    try {
      const ws = new WebSocket(makeWsUrl("/ws"));
      wsRef.current = ws;
      ws.addEventListener("message", (ev) => {
        try {
          const msg = JSON.parse(ev.data || "{}");
          if (msg?.type !== "role_changed") return;
          const target = Number(msg?.payload?.tg_id);
          if (!myIdRef.current || target !== myIdRef.current) return;
          refreshRoleAndReact();
        } catch {}
      });
      ws.addEventListener("close", () => { wsRef.current = null; });
    } catch {}

    pingRef.current = window.setInterval(refreshRoleAndReact, 15000);

    return () => {
      alive = false;
      if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
    };
  }, []);

  async function refreshRoleAndReact() {
    try {
      const me = await api.me();
      myIdRef.current = me.me?.id ?? null;
      const role = normalizeRole(me.role);
      const prev = prevRoleRef.current;

      if (role === prev) return;

      window.dispatchEvent(new CustomEvent("app:role-changed", { detail: { role } }));
      prevRoleRef.current = role;

      routeForRole(role, true);
    } catch {}
  }

  function routeForRole(role: Role, forceRemountWhenSameGroup: boolean) {
    const r = ROUTE.parse();

    // где мы «уже на супер»
    const inSuper =
      ROUTE.isSuperHome(r) ||
      ROUTE.isSuperEventsHome(r) ||
      ROUTE.isSuperEventNew(r) ||
      ROUTE.isSuperEventEdit(r) ||
      ROUTE.isSuperStaffHome(r) ||
      ROUTE.isSuperStaffRoles(r) ||
      ROUTE.isSuperMessages(r);

    // где мы «уже на двери»
    const inDoor =
      ROUTE.isDoorHome(r) ||
      ROUTE.isDoorScan(r) ||
      ROUTE.isDoorToken(r);

    // ---- навигация по ролям
    if (role === "owner" || role === "superadmin" || role === "admin") {
      if (!inSuper || forceRemountWhenSameGroup) ROUTE.go(ROUTE.super(), true);
      return;
    }
    if (role === "door") {
      if (!inDoor || forceRemountWhenSameGroup) ROUTE.go(ROUTE.door(), true);
      return;
    }

    // Гость (или неизвестно): не сбиваем экран EULA.
    const onGuestHome = ROUTE.isGuestHome(r);
    const onGuestEula = ROUTE.isGuestEula(r);
    const onRoot = r.name === "root";

    if (onGuestEula) {
      // уже читаем оферту — остаёмся.
      return;
    }

    if (!(onGuestHome || onRoot) || forceRemountWhenSameGroup) {
      ROUTE.go(ROUTE.guest(), true);
    }
  }

  return null;
}

function makeWsUrl(path: string): string {
  const base = window.location.origin.replace(/^http/, "ws");
  return `${base}${path}`;
}